<template>
  <div class="relative w-full h-full flex">
    <!-- No Data Available -->
    <div v-if="noData" class="absolute inset-0 flex flex-col items-center justify-center pb-12">
      <CalendarScheduleIcon class="text-icon-normal" />
      <BaseText type="label" size="sm" class="text-text-normal mt-3">
        No Velocity Data Available
      </BaseText>
      <BaseText type="body" size="sm" class="text-text-normal mt-1.5 text-center" style="max-width: 260px;">
        This brand doesn't have any creative velocity data available.
      </BaseText>
    </div>
    <!-- Left Y-Axis Labels -->
    <div class="h-full flex flex-col justify-between px-4 pb-10 flex-shrink-0" :class="{ 'opacity-0': noData }">
      <BaseText v-for="(label, index) in leftYAxisLabels" :key="`left-y-axis-label-${index}`"
      type="body" size="xs" class="text-text-subdued">
        {{ label }}
      </BaseText>
    </div>
    <div class="flex-grow h-full flex flex-col px-3">
      <!-- Graph Area -->
      <div class="flex-grow w-full py-2">
        <div class="relative w-full h-full">
          <!-- Plot Labels -->
          <div v-if="!noData" class="absolute top-0 bottom-0 -left-3 -right-3 flex flex-col justify-between">
            <div v-for="n in leftYAxisLabels.length" :key="`left-y-axis-plot-label-${n}`" 
            class="w-full h-0 border-t border-border-normal" />
          </div>
          <!-- Line Graph (Total Active) -->
          <div v-if="showLineGraph && !noActiveCreativesData" class="absolute inset-0 slow-fade-in">
            <LineGraphRenderer 
              :values="graphData.map(item => item.active_count)"
              :scale="rightYAxisScale"
            />
          </div>
          <!-- Top Bar Graph (New Launched) -->
          <div v-if="!noLaunchOrRetiredData" class="absolute bottom-1/2 left-0 right-0 top-0">
            <BarGraphRenderer 
              :values="graphData.map(item => item.launched_count)" 
              :scale="leftYAxisScale"
            />
          </div>
          <!-- Bottom Bar Graph (Retired Paused) -->
          <div v-if="!noLaunchOrRetiredData" class="absolute bottom-0 left-0 right-0 top-1/2">
            <BarGraphRenderer 
              :values="graphData.map(item => item.paused_count)"
              :scale="leftYAxisScale"
              mirror
            />
          </div>
        </div>
      </div>
      <!-- Date X-Axis Labels -->
      <div class="w-full h-10 flex items-center justify-between flex-shrink-0">
        <div v-for="(label, index) in dateLabels" :key="`velocity-date-label-${index}`"
        class="relative w-0 h-full">
          <BaseText type="body" size="xs" :class="{ 'hidden 2.75xl:block': datesResponsiveModulus && index % datesResponsiveModulus !== 0 }"
          class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-text-subdued whitespace-nowrap">
            {{ label }}
          </BaseText>
        </div>
      </div>
    </div>
    <!-- Right Y-Axis Labels -->
    <div class="h-full flex flex-col justify-between px-4 pb-10 flex-shrink-0 text-right" :class="{ 'opacity-0': noData }">
      <BaseText v-for="(label, index) in rightYAxisLabels" :key="`right-y-axis-label-${index}`"
      type="body" size="xs" class="text-text-subdued">
        {{ label }}
      </BaseText>
    </div>
  </div>
</template>

<script>
import { scaleLinear } from 'd3-scale'
import formatLabelString from '@/utils/lens/formatGraphLabelString'
import moment from 'moment'

import LineGraphRenderer from './LineGraphRenderer.vue'
import BarGraphRenderer from './BarGraphRenderer.vue'

import CalendarScheduleIcon from '@/components/globals/Icons/CalendarScheduleIcon.vue'

export default {
  name: 'VelocityGraph',
  components: {
    LineGraphRenderer,
    BarGraphRenderer,
    CalendarScheduleIcon
  },
  props: {
    graphData: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      leftYAxisScale: null,
      rightYAxisScale: null,
      leftYAxisLabels: [],
      rightYAxisLabels: [],
      dateLabels: [],
      datesResponsiveModulus: 0,
      showLineGraph: false
    }
  },
  computed: {
    noLaunchOrRetiredData () {
      return this.graphData?.every(item => item.launched_count === 0 && item.paused_count === 0)
    },
    noActiveCreativesData () {
      return this.graphData?.every(item => item.active_count === 0)
    },
    noData () {
      return this.noLaunchOrRetiredData && this.noActiveCreativesData
    }
  },
  mounted () {
    this.computeAxisScales()
    // We delay the mount for the line graph (aesthetic)
    setTimeout(() => { this.showLineGraph = true }, 500)
  },
  methods: {
    computeAxisScales () {
      // Find the max value among the launched_count and paused_count fields
      const leftMax = Math.max(
        ...this.graphData.map(item => item.launched_count),
        ...this.graphData.map(item => item.paused_count),
        5 // Make sure the max is at least 5
      )
      // Create the left Y-axis scale
      const leftScale = scaleLinear().domain([0, leftMax]).nice()
      this.leftYAxisScale = leftScale

      // Compute the labels for the left Y-axis
      let leftTicks = leftScale.ticks(7)

      // Process the ticks to ensure domain max covers leftMax, and that num ticks <= 7
      const addTick = (ticks) => {
        const newMax = ticks[ticks.length - 1] + ticks[1]
        ticks.push(newMax)
        leftScale.domain([0, newMax])
        return ticks
      }
      const reduceTicks = (ticks) => {
        let tickValues = [...ticks]
        if (ticks[ticks.length - 1] < leftMax) tickValues = addTick(tickValues)
        while (tickValues.length > 7) {
          if (tickValues.length % 2 === 0) tickValues = addTick(tickValues)
          tickValues = tickValues.filter((_, index) => index % 2 === 0)
        }
        return tickValues
      }
      leftTicks = reduceTicks(leftTicks)

      const reversedLeftTicks = [...leftTicks].reverse()
      // We are measuring in both directions, so mirror the axis labels
      this.leftYAxisLabels = [...reversedLeftTicks, ...leftTicks.slice(1)].map(tick =>
        formatLabelString(tick, 'number')
      )

      // Create the right Y axis scale
      const rightMax = Math.max(...this.graphData.map(item => item.active_count))
      const rightScale = scaleLinear().domain([0, rightMax]).nice()
      this.rightYAxisScale = rightScale

      // Compute the labels for the right Y-axis - Get a rounded max using the scale
      const [_, d1] = rightScale.domain()
      const rightLabelMax = d1
      const numRightLabels = this.leftYAxisLabels.length

      const step = rightLabelMax / (numRightLabels - 1)
      const rightTicks = Array.from({ length: numRightLabels }, (_, index) => index * step)
      this.rightYAxisLabels = rightTicks.map(tick =>
        formatLabelString(d1 > 10 ? Math.round(tick) : tick, 'number')
      ).reverse()

      // Compute the date labels for the X-axis
      const rawDates = this.graphData.map(item => item.event_date)
      // Think the array to the base level max number of labels
      const numDates = rawDates.length
      let thinnedDates = rawDates
      if (numDates === 15) {
        this.datesResponsiveModulus = 2
      } else if (numDates === 31) {
        this.datesResponsiveModulus = 3
        thinnedDates = thinArray(rawDates, 2, 1)
      } else if (numDates === 61) {
        this.datesResponsiveModulus = 3
        thinnedDates = thinArray(rawDates, 2, 2)
      }
      this.dateLabels = thinnedDates.map(date => moment(date).format('MMM D'))
    }
  }
}

const thinArray = (array, modulus, runs) => {
  let arr = [...array]
  for (let i = 0; i < runs; i++) {
    arr = arr.filter((_, index) => index % modulus === 0)
  }
  return arr
}
</script>

<style scoped>
.slow-fade-in {
  animation: slowFadeIn 500ms ease-in-out;
}
@keyframes slowFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>